import {
  TailwindButton,
  TailwindPopover,
  TailwindPopoverContent,
  TailwindPopoverTrigger,
} from '@autone/ui';
import { useGetElementById } from '@autone/utils';
import { CogIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useExtrasMenuTypeGuards } from '../../hooks/useExtrasMenuTypeGuards';
import { type TableNamesWithExtrasMenu } from '../../storage/session-storage';

import { AssortmentCustomColumnsMenu } from './assortment-custom-columns-menu';
import { ManagedByAutoneToggle } from './managed-by-autone-toggle';
import { MatchSystemRecommendationToggle } from './match-system-recommendation-toggle/MatchSystemRecommendationToggle';

interface AssortmentExtrasMenuProps {
  tableName: TableNamesWithExtrasMenu;
  align: 'start' | 'center' | 'end';
}

export const AssortmentExtrasMenu = ({
  tableName,
  align,
}: AssortmentExtrasMenuProps) => {
  const {
    isTableWithCustomColumns,
    isTableWithManagedByAutoneToggle,
    isTableWithMatchSystemRecommendationToggle,
  } = useExtrasMenuTypeGuards(tableName);
  const [open, setOpen] = useState(false);
  const dashboardLayoutRef = useGetElementById('dashboard-layout');

  useEffect(() => {
    const dashboardLayout = dashboardLayoutRef.current;
    if (!dashboardLayout) return;
    const handleScroll = () => {
      setOpen(false);
    };
    dashboardLayout.addEventListener('scroll', handleScroll);
    return () => {
      dashboardLayout.removeEventListener('scroll', handleScroll);
    };
  }, [open, dashboardLayoutRef]);

  return (
    <TailwindPopover open={open} onOpenChange={setOpen}>
      <TailwindPopoverTrigger asChild>
        <TailwindButton
          className="w-12 h-12"
          variant={'outlined'}
          color={'secondary'}
          size={'icon'}
        >
          <CogIcon />
        </TailwindButton>
      </TailwindPopoverTrigger>
      <TailwindPopoverContent
        dialog
        className="flex flex-col items-start p-1 w-[300px]"
        align={align}
      >
        {isTableWithCustomColumns(tableName) && (
          <AssortmentCustomColumnsMenu tableName={tableName} />
        )}
        {isTableWithManagedByAutoneToggle(tableName) && (
          <ManagedByAutoneToggle tableName={tableName} />
        )}
        {isTableWithMatchSystemRecommendationToggle(tableName) && (
          <MatchSystemRecommendationToggle tableName={tableName} />
        )}
      </TailwindPopoverContent>
    </TailwindPopover>
  );
};
